import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import VoiceDictationInput from '../components/VoiceDictationInput';
import { 
  FileText, Save, ArrowLeft, ArrowRight, User, Brain, Heart, 
  Activity, Pill, Stethoscope, Home, CheckCircle, Download, Printer
} from 'lucide-react';
import { generateOasisPDF, downloadPDF, printPDF } from '../utils/pdfExport';
import { cachePdfPatients } from '../utils/fillable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OasisAssessmentForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const formId = searchParams.get('formId');
  const assessmentType = searchParams.get('type') || 'SOC';
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState({
    patientId: patientId || '',
    patientName: '',
    assessmentType: assessmentType,
    assessmentDate: new Date().toISOString().split('T')[0],
    // Section A: Administrative
    npi: '',
    physicianName: '',
    physicianPhone: '',
    medicareNumber: '',
    medicaidNumber: '',
    gender: '',
    ethnicity: [],
    race: [],
    paymentSources: [],
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    primaryCaregiverName: '',
    caregiverAvailable: null,
    caregiverNeedsTraining: null,
    hasLivingWill: null,
    hasDNR: null,
    hasMedicalPOA: null,
    medicalPOAName: '',
    // Section B: Hearing, Speech, Vision
    hearingStatus: null,
    visionStatus: null,
    healthLiteracy: null,
    // Section C: Cognitive (BIMS)
    bimsRepetition: null,
    bimsYear: null,
    bimsMonth: null,
    bimsDay: null,
    bimsRecallSock: null,
    bimsRecallBlue: null,
    bimsRecallBed: null,
    bimsTotal: null,
    cognitiveFunction: null,
    // Section D: Mood (PHQ-9)
    phqLittleInterest: null,
    phqFeelingDown: null,
    phqSleepTrouble: null,
    phqFeelingTired: null,
    phqAppetite: null,
    phqFeelingBad: null,
    phqConcentration: null,
    phqMovingSpeaking: null,
    phqSuicidalThoughts: null,
    phqTotal: null,
    socialIsolation: null,
    // Section GG: Functional
    selfCareScore: null,
    mobilityScore: null,
    transferScore: null,
    walkingScore: null,
    // Section J: Health
    painLevel: null,
    painFrequency: null,
    fallRisk: null,
    fallsLastAssessment: null,
    // Section K: Nutrition
    heightInches: null,
    weightLbs: null,
    nutritionalApproaches: [],
    // Section M: Skin
    hasPressureUlcer: null,
    hasStasisUlcer: null,
    hasSurgicalWound: null,
    // Section N: Medications
    highRiskDrugs: [],
    drugRegimenReviewIssues: null,
    oralMedManagement: null,
    injectableMedManagement: null,
    // Section O: Treatments
    specialTreatments: [],
    therapyNeed: null,
    // Homebound
    homeboundCriteriaOne: [],
    homeboundCriteriaTwo: '',
    additionalComments: ''
  });

  const sections = [
    { name: 'Administrative', icon: User },
    { name: 'Hearing/Vision', icon: Activity },
    { name: 'Cognitive (BIMS)', icon: Brain },
    { name: 'Mood (PHQ-9)', icon: Heart },
    { name: 'Functional', icon: Activity },
    { name: 'Health Conditions', icon: Stethoscope },
    { name: 'Medications', icon: Pill },
    { name: 'Homebound Status', icon: Home },
  ];

  useEffect(() => {
    fetchPatients();
    if (formId) {
      fetchForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  useEffect(() => {
    // Calculate BIMS total
    const bimsTotal = (formData.bimsRepetition || 0) + 
                      (formData.bimsYear || 0) + 
                      (formData.bimsMonth || 0) + 
                      (formData.bimsDay || 0) + 
                      (formData.bimsRecallSock || 0) + 
                      (formData.bimsRecallBlue || 0) + 
                      (formData.bimsRecallBed || 0);
    if (bimsTotal !== formData.bimsTotal) {
      setFormData(prev => ({ ...prev, bimsTotal }));
    }
  }, [formData.bimsRepetition, formData.bimsYear, formData.bimsMonth, formData.bimsDay, 
      formData.bimsRecallSock, formData.bimsRecallBlue, formData.bimsRecallBed]);

  useEffect(() => {
    // Calculate PHQ total
    const phqTotal = (formData.phqLittleInterest || 0) + 
                     (formData.phqFeelingDown || 0) + 
                     (formData.phqSleepTrouble || 0) + 
                     (formData.phqFeelingTired || 0) + 
                     (formData.phqAppetite || 0) + 
                     (formData.phqFeelingBad || 0) + 
                     (formData.phqConcentration || 0) + 
                     (formData.phqMovingSpeaking || 0) + 
                     (formData.phqSuicidalThoughts || 0);
    if (phqTotal !== formData.phqTotal) {
      setFormData(prev => ({ ...prev, phqTotal }));
    }
  }, [formData.phqLittleInterest, formData.phqFeelingDown, formData.phqSleepTrouble,
      formData.phqFeelingTired, formData.phqAppetite, formData.phqFeelingBad,
      formData.phqConcentration, formData.phqMovingSpeaking, formData.phqSuicidalThoughts]);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPatients(data.patients); cachePdfPatients(data.patients);
        if (patientId) {
          const patient = data.patients.find(p => p.id === patientId);
          if (patient) {
            setFormData(prev => ({
              ...prev,
              patientName: `${patient.firstName} ${patient.lastName}`
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchForm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/oasis/${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setFormData(data.assessment);
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const handlePatientChange = (e) => {
    const selectedPatientId = e.target.value;
    const patient = patients.find(p => p.id === selectedPatientId);
    if (patient) {
      setFormData(prev => ({
        ...prev,
        patientId: selectedPatientId,
        patientName: `${patient.firstName} ${patient.lastName}`
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...(prev[field] || []), value]
        : (prev[field] || []).filter(v => v !== value)
    }));
  };

  const handleSave = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = formId 
        ? `${API_URL}/api/forms/oasis/${formId}`
        : `${API_URL}/api/forms/oasis`;
      
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Assessment updated' : 'Assessment created');
        if (!formId && data.assessment) {
          navigate(`/dashboard/clinical/oasis?formId=${data.assessment.id}`);
        }
      } else {
        toast.error('Failed to save assessment');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!formId) {
      toast.error('Please save the assessment first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/oasis/${formId}/complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Assessment completed successfully');
        navigate('/dashboard/clinical/oasis');
      }
    } catch (error) {
      toast.error('Failed to complete assessment');
    }
  };

  const handleExportPDF = () => {
    const doc = generateOasisPDF(formData);
    downloadPDF(doc, `oasis-${formData.assessmentType}-${formData.patientName || 'assessment'}.pdf`);
  };

  const handlePrintPDF = () => {
    const doc = generateOasisPDF(formData);
    printPDF(doc);
  };

  const renderSection = () => {
    switch (currentSection) {
      case 0: // Administrative
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Patient *</Label>
                <select
                  value={formData.patientId}
                  onChange={handlePatientChange}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12]"
                >
                  <option value="">Select a patient</option>
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.firstName} {patient.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Assessment Type</Label>
                <select
                  value={formData.assessmentType}
                  onChange={(e) => handleInputChange('assessmentType', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12]"
                >
                  <option value="SOC">Start of Care (SOC)</option>
                  <option value="ROC">Resumption of Care (ROC)</option>
                  <option value="Recertification">Recertification</option>
                </select>
              </div>
              <div>
                <Label>Assessment Date</Label>
                <Input
                  type="date"
                  value={formData.assessmentDate}
                  onChange={(e) => handleInputChange('assessmentDate', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Physician NPI</Label>
                <Input
                  value={formData.npi || ''}
                  onChange={(e) => handleInputChange('npi', e.target.value)}
                  placeholder="National Provider Identifier"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Physician Name</Label>
                <Input
                  value={formData.physicianName || ''}
                  onChange={(e) => handleInputChange('physicianName', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Physician Phone</Label>
                <Input
                  value={formData.physicianPhone || ''}
                  onChange={(e) => handleInputChange('physicianPhone', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">Payment Sources</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {['Medicare', 'Medicaid', 'Private Insurance', 'HMO', 'Self-Pay', 'Other'].map(source => (
                  <label key={source} className="flex items-center gap-2">
                    <Checkbox
                      checked={(formData.paymentSources || []).includes(source)}
                      onCheckedChange={(checked) => handleArrayChange('paymentSources', source, checked)}
                    />
                    <span className="text-sm">{source}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">Emergency Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={formData.emergencyContactName || ''}
                    onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.emergencyContactPhone || ''}
                    onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Relationship</Label>
                  <Input
                    value={formData.emergencyContactRelationship || ''}
                    onChange={(e) => handleInputChange('emergencyContactRelationship', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">Advance Directives</h3>
              <div className="space-y-3">
                {[
                  { field: 'hasLivingWill', label: 'Has Living Will' },
                  { field: 'hasDNR', label: 'Has DNR Order' },
                  { field: 'hasMedicalPOA', label: 'Has Medical Power of Attorney' }
                ].map(({ field, label }) => (
                  <div key={field} className="flex items-center gap-4">
                    <span className="w-48 text-sm">{label}</span>
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        checked={formData[field] === true}
                        onChange={() => handleInputChange(field, true)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        checked={formData[field] === false}
                        onChange={() => handleInputChange(field, false)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 1: // Hearing/Vision
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-3">B0200. Hearing</h3>
              <p className="text-sm text-gray-600 mb-3">Ability to hear (with hearing aid if normally used)</p>
              <div className="space-y-2">
                {[
                  { value: 0, label: '0 - Adequate: No difficulty in normal conversation' },
                  { value: 1, label: '1 - Minimal difficulty: Difficulty in some environments' },
                  { value: 2, label: '2 - Moderate difficulty: Speaker has to increase volume' },
                  { value: 3, label: '3 - Highly impaired: Absence of useful hearing' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={formData.hearingStatus === option.value}
                      onChange={() => handleInputChange('hearingStatus', option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">B1000. Vision</h3>
              <p className="text-sm text-gray-600 mb-3">Ability to see in adequate light (with glasses if normally used)</p>
              <div className="space-y-2">
                {[
                  { value: 0, label: '0 - Adequate: Sees fine detail' },
                  { value: 1, label: '1 - Impaired: Sees large print, but not newspaper headlines' },
                  { value: 2, label: '2 - Moderately impaired: Limited vision' },
                  { value: 3, label: '3 - Highly impaired: Object identification questionable' },
                  { value: 4, label: '4 - Severely impaired: No vision or light perception only' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={formData.visionStatus === option.value}
                      onChange={() => handleInputChange('visionStatus', option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">B1300. Health Literacy</h3>
              <p className="text-sm text-gray-600 mb-3">How often do you need help reading medical instructions?</p>
              <div className="space-y-2">
                {[
                  { value: 0, label: '0 - Never' },
                  { value: 1, label: '1 - Rarely' },
                  { value: 2, label: '2 - Sometimes' },
                  { value: 3, label: '3 - Often' },
                  { value: 4, label: '4 - Always' },
                  { value: 7, label: '7 - Patient declines to respond' },
                  { value: 8, label: '8 - Patient unable to respond' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={formData.healthLiteracy === option.value}
                      onChange={() => handleInputChange('healthLiteracy', option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 2: // Cognitive (BIMS)
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-blue-900">Brief Interview for Mental Status (BIMS)</h3>
              <p className="text-sm text-blue-700 mt-1">Score range: 0-15. Higher scores indicate better cognitive function.</p>
            </div>

            <div>
              <h3 className="font-medium mb-3">C0200. Repetition of Three Words</h3>
              <p className="text-sm text-gray-600 mb-3">"I am going to say three words: sock, blue, bed. Please repeat them."</p>
              <div className="space-y-2">
                {[
                  { value: 0, label: '0 - None' },
                  { value: 1, label: '1 - One word' },
                  { value: 2, label: '2 - Two words' },
                  { value: 3, label: '3 - Three words' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={formData.bimsRepetition === option.value}
                      onChange={() => handleInputChange('bimsRepetition', option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">C0300. Temporal Orientation</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">A. "What year is it right now?"</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 0, label: 'Missed by >5 years' },
                      { value: 1, label: 'Missed by 2-5 years' },
                      { value: 2, label: 'Missed by 1 year' },
                      { value: 3, label: 'Correct' }
                    ].map(option => (
                      <label key={option.value} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                        <input
                          type="radio"
                          checked={formData.bimsYear === option.value}
                          onChange={() => handleInputChange('bimsYear', option.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">B. "What month are we in?"</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 0, label: 'Missed by >1 month' },
                      { value: 1, label: 'Missed by 6 days-1 month' },
                      { value: 2, label: 'Accurate within 5 days' }
                    ].map(option => (
                      <label key={option.value} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                        <input
                          type="radio"
                          checked={formData.bimsMonth === option.value}
                          onChange={() => handleInputChange('bimsMonth', option.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">C. "What day of the week is today?"</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 0, label: 'Incorrect' },
                      { value: 1, label: 'Correct' }
                    ].map(option => (
                      <label key={option.value} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                        <input
                          type="radio"
                          checked={formData.bimsDay === option.value}
                          onChange={() => handleInputChange('bimsDay', option.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">C0400. Recall</h3>
              <p className="text-sm text-gray-600 mb-3">"What were those three words I asked you to repeat?"</p>
              
              {['Sock', 'Blue', 'Bed'].map((word, idx) => (
                <div key={word} className="mb-3">
                  <p className="text-sm font-medium mb-2">Recall "{word}"</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 0, label: 'Could not recall' },
                      { value: 1, label: 'Yes, after cue' },
                      { value: 2, label: 'Yes, no cue needed' }
                    ].map(option => (
                      <label key={option.value} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                        <input
                          type="radio"
                          checked={formData[`bimsRecall${word}`] === option.value}
                          onChange={() => handleInputChange(`bimsRecall${word}`, option.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-medium text-green-900">C0500. BIMS Summary Score</h3>
              <p className="text-3xl font-bold text-green-700 mt-2">{formData.bimsTotal || 0} / 15</p>
              <p className="text-sm text-green-600 mt-1">
                {formData.bimsTotal >= 13 ? 'Intact cognition' : 
                 formData.bimsTotal >= 8 ? 'Moderate impairment' : 'Severe impairment'}
              </p>
            </div>
          </div>
        );

      case 3: // Mood (PHQ-9)
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-medium text-purple-900">Patient Health Questionnaire (PHQ-9)</h3>
              <p className="text-sm text-purple-700 mt-1">Over the last 2 weeks, how often have you been bothered by these problems?</p>
            </div>

            <div className="text-sm text-gray-600 mb-4">
              <p className="font-medium">Frequency Scale:</p>
              <p>0 = Not at all | 1 = Several days | 2 = More than half the days | 3 = Nearly every day</p>
            </div>

            {[
              { field: 'phqLittleInterest', label: 'Little interest or pleasure in doing things' },
              { field: 'phqFeelingDown', label: 'Feeling down, depressed, or hopeless' },
              { field: 'phqSleepTrouble', label: 'Trouble falling/staying asleep, or sleeping too much' },
              { field: 'phqFeelingTired', label: 'Feeling tired or having little energy' },
              { field: 'phqAppetite', label: 'Poor appetite or overeating' },
              { field: 'phqFeelingBad', label: 'Feeling bad about yourself' },
              { field: 'phqConcentration', label: 'Trouble concentrating' },
              { field: 'phqMovingSpeaking', label: 'Moving or speaking slowly, or being restless' },
              { field: 'phqSuicidalThoughts', label: 'Thoughts of self-harm' }
            ].map(({ field, label }) => (
              <div key={field} className="border-b pb-3">
                <p className="text-sm font-medium mb-2">{label}</p>
                <div className="flex gap-4">
                  {[0, 1, 2, 3].map(value => (
                    <label key={value} className="flex items-center gap-1">
                      <input
                        type="radio"
                        checked={formData[field] === value}
                        onChange={() => handleInputChange(field, value)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{value}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-medium text-purple-900">D0160. PHQ-9 Total Score</h3>
              <p className="text-3xl font-bold text-purple-700 mt-2">{formData.phqTotal || 0} / 27</p>
              <p className="text-sm text-purple-600 mt-1">
                {formData.phqTotal >= 20 ? 'Severe depression' : 
                 formData.phqTotal >= 15 ? 'Moderately severe depression' :
                 formData.phqTotal >= 10 ? 'Moderate depression' :
                 formData.phqTotal >= 5 ? 'Mild depression' : 'Minimal depression'}
              </p>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">D0700. Social Isolation</h3>
              <p className="text-sm text-gray-600 mb-3">How often do you feel lonely or isolated?</p>
              <div className="space-y-2">
                {[
                  { value: 0, label: '0 - Never' },
                  { value: 1, label: '1 - Rarely' },
                  { value: 2, label: '2 - Sometimes' },
                  { value: 3, label: '3 - Often' },
                  { value: 4, label: '4 - Always' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={formData.socialIsolation === option.value}
                      onChange={() => handleInputChange('socialIsolation', option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 4: // Functional
        return (
          <div className="space-y-6">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h3 className="font-medium text-orange-900">Section GG: Functional Abilities</h3>
              <p className="text-sm text-orange-700 mt-1">Rate the patient's ability to perform activities</p>
            </div>

            <div className="text-sm text-gray-600 mb-4 p-3 bg-gray-50 rounded">
              <p className="font-medium">Scoring Scale (06-01):</p>
              <p>06 = Independent | 05 = Setup/cleanup | 04 = Supervision | 03 = Partial/moderate assistance | 02 = Substantial assistance | 01 = Dependent</p>
            </div>

            {[
              { field: 'selfCareScore', label: 'Self-Care (eating, grooming, bathing, dressing)' },
              { field: 'mobilityScore', label: 'Mobility (lying to sitting, sitting to standing)' },
              { field: 'transferScore', label: 'Transfers (bed to chair, toilet transfer)' },
              { field: 'walkingScore', label: 'Walking (walking 10 feet, walking 50 feet, stairs)' }
            ].map(({ field, label }) => (
              <div key={field} className="border-b pb-4">
                <p className="text-sm font-medium mb-3">{label}</p>
                <div className="flex flex-wrap gap-2">
                  {[6, 5, 4, 3, 2, 1].map(value => (
                    <label key={value} className="flex items-center gap-1 p-2 bg-gray-50 rounded min-w-[60px]">
                      <input
                        type="radio"
                        checked={formData[field] === value}
                        onChange={() => handleInputChange(field, value)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">0{value}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case 5: // Health Conditions
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-3">J0300. Pain Assessment</h3>
              <p className="text-sm text-gray-600 mb-3">Rate pain on a scale of 0-10</p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="10"
                  value={formData.painLevel || ''}
                  onChange={(e) => handleInputChange('painLevel', parseInt(e.target.value) || null)}
                  className="w-24"
                  placeholder="0-10"
                />
                <span className="text-sm text-gray-500">/ 10</span>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">J1800. Fall Risk Assessment</h3>
              <p className="text-sm text-gray-600 mb-3">Any falls since SOC/ROC?</p>
              <div className="space-y-2">
                {[
                  { value: 0, label: '0 - No falls' },
                  { value: 1, label: '1 - One fall, no injury' },
                  { value: 2, label: '2 - One fall, injury' },
                  { value: 3, label: '3 - Two+ falls, no injury' },
                  { value: 4, label: '4 - Two+ falls, injury' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={formData.fallRisk === option.value}
                      onChange={() => handleInputChange('fallRisk', option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">Section K: Nutritional Status</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Height (inches)</Label>
                  <Input
                    type="number"
                    value={formData.heightInches || ''}
                    onChange={(e) => handleInputChange('heightInches', parseFloat(e.target.value) || null)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Weight (lbs)</Label>
                  <Input
                    type="number"
                    value={formData.weightLbs || ''}
                    onChange={(e) => handleInputChange('weightLbs', parseFloat(e.target.value) || null)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">Section M: Skin Conditions</h3>
              <div className="space-y-3">
                {[
                  { field: 'hasPressureUlcer', label: 'Pressure ulcer at Stage 2 or higher?' },
                  { field: 'hasStasisUlcer', label: 'Stasis ulcer?' },
                  { field: 'hasSurgicalWound', label: 'Surgical wound?' }
                ].map(({ field, label }) => (
                  <div key={field} className="flex items-center gap-4">
                    <span className="w-64 text-sm">{label}</span>
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        checked={formData[field] === true}
                        onChange={() => handleInputChange(field, true)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        checked={formData[field] === false}
                        onChange={() => handleInputChange(field, false)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 6: // Medications
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-3">N0415. High-Risk Drug Classes</h3>
              <p className="text-sm text-gray-600 mb-3">Check all that apply</p>
              <div className="grid grid-cols-2 gap-2">
                {['Antipsychotic', 'Anticoagulant', 'Antibiotic', 'Opioid', 'Antiplatelet', 'Hypoglycemic'].map(drug => (
                  <label key={drug} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <Checkbox
                      checked={(formData.highRiskDrugs || []).includes(drug)}
                      onCheckedChange={(checked) => handleArrayChange('highRiskDrugs', drug, checked)}
                    />
                    <span className="text-sm">{drug}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">M2001. Drug Regimen Review</h3>
              <p className="text-sm text-gray-600 mb-3">Were any medication issues identified?</p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.drugRegimenReviewIssues === false}
                    onChange={() => handleInputChange('drugRegimenReviewIssues', false)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">No issues found</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.drugRegimenReviewIssues === true}
                    onChange={() => handleInputChange('drugRegimenReviewIssues', true)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Issues found</span>
                </label>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">M2020. Management of Oral Medications</h3>
              <div className="space-y-2">
                {[
                  { value: 0, label: '0 - Able to independently take correct medication' },
                  { value: 1, label: '1 - Able if dosages prepared or chart developed' },
                  { value: 2, label: '2 - Able if given reminders' },
                  { value: 3, label: '3 - Unable unless administered by another' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={formData.oralMedManagement === option.value}
                      onChange={() => handleInputChange('oralMedManagement', option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 7: // Homebound Status
        return (
          <div className="space-y-6">
            <div className="bg-teal-50 p-4 rounded-lg">
              <h3 className="font-medium text-teal-900">Homebound Status Assessment</h3>
              <p className="text-sm text-teal-700 mt-1">Patient must meet Criteria 1 AND 2 to be considered homebound</p>
            </div>

            <div>
              <h3 className="font-medium mb-3">Criteria 1: Due to illness/injury, patient is:</h3>
              <div className="space-y-2">
                {[
                  'Dependent on walker',
                  'Dependent on wheelchair',
                  'Dependent on cane/crutches',
                  'Needs special transportation',
                  'Needs physical assistance to leave',
                  'Leaving home is medically contraindicated'
                ].map(criteria => (
                  <label key={criteria} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <Checkbox
                      checked={(formData.homeboundCriteriaOne || []).includes(criteria)}
                      onCheckedChange={(checked) => handleArrayChange('homeboundCriteriaOne', criteria, checked)}
                    />
                    <span className="text-sm">{criteria}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">Criteria 2: Leaving home requires considerable and taxing effort</h3>
              <textarea
                value={formData.homeboundCriteriaTwo || ''}
                onChange={(e) => handleInputChange('homeboundCriteriaTwo', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12]"
                rows={3}
                placeholder="Describe why leaving home requires considerable effort..."
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">Additional Comments</h3>
              <VoiceDictationInput
                value={formData.additionalComments || ''}
                onChange={(e) => handleInputChange('additionalComments', e.target.value)}
                rows={4}
                placeholder="Any additional assessment notes..."
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f39c12]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard/clinical/oasis')}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#f39c12]" />
                  Non-OASIS {formData.assessmentType} Assessment
                </h1>
                <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                  Comprehensive Adult Nursing Assessment
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={handleExportPDF}
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" /> PDF
              </Button>
              <Button 
                variant="outline"
                onClick={handlePrintPDF}
                data-testid="print-pdf-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                variant="outline"
              >
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save</>}
              </Button>
              {formId && (
                <Button 
                  onClick={handleComplete}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Complete
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Section Navigation */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <button
                  key={index}
                  onClick={() => setCurrentSection(index)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentSection === index
                      ? 'bg-[#f39c12] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {section.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          {renderSection()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mb-8">
          <Button
            variant="outline"
            onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
            disabled={currentSection === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>
          <span className="text-sm text-gray-500">
            Section {currentSection + 1} of {sections.length}
          </span>
          <Button
            onClick={() => setCurrentSection(prev => Math.min(sections.length - 1, prev + 1))}
            disabled={currentSection === sections.length - 1}
            className="bg-[#f39c12] hover:bg-[#e67e22] text-white"
          >
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OasisAssessmentForm;
